import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import axios from 'axios'
import { ChargingCardDto } from './dto/charging-cards-dto'
import { assignTokenApiErrors } from './dto/assign-token-api-erros'
import { AssignTokenLinkDataDto } from './dto/assign-token-dto'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

interface UseLinkTokenHook {
  isLoading: boolean
  assignToken: (payload: AssignTokenLinkDataDto) => void
  onSuccess?: () => void
  onError?: () => void
}

/**
 * Function for assign token to a wallbox
 * @param onSuccess - function for closing dialog on success method
 * @param onError - function for closing dialog on error method
 * @param installationId - unique installation identifier as a string
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useAssignToken = (installationId: number, onSuccess: (newData: ChargingCardDto) => void, onError?: () => void): UseLinkTokenHook => {
  const { t } = useCustomTranslation()
  const queryClient = useQueryClient()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const assignTokenApi = async (payload: AssignTokenLinkDataDto): Promise<ApiResult<ChargingCardDto>> => {
    const assignTariffResponse = await authFetch<ChargingCardDto>({
      path: `tokens/wallboxes/${payload.installationId}/${payload.tokenId}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'PUT',
      payload: payload.wallboxes,
    })

    return new ApiResult(assignTariffResponse)
  }

  const { isPending, mutate: assignToken } = usePortalMutation({
    mutationFn: assignTokenApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(assignTokenApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.wallboxes.assign-token-failed'))
      }
    },
    onSuccess: newData => {
      enqueueSuccessStack(t('emobility.wallboxes.assign-token-success'))
      queryClient.invalidateQueries({ queryKey: ['chargingCards', installationId] })
      queryClient.invalidateQueries({ queryKey: ['wallboxes', installationId] })
      if (newData) onSuccess(newData)
    },
  })

  return { isLoading: isPending, assignToken }
}
